export const MIN_ZOOM = 40;
export const MAX_ZOOM = 200;
export const DEFAULT_ZOOM = 100;
export const ZOOM_STEP = 10;
export const FIT_PADDING = 48;

/** Subtle GHL-style canvas grid tokens. */
export const WORKFLOW_CANVAS_GRID = {
  backgroundColor: '#F8FAFC',
  lineColor: 'rgba(148, 163, 184, 0.16)',
  size: 28,
};

/** Connector line styling shared by step/trigger paths. */
export const WORKFLOW_CONNECTOR_LINE = {
  width: 2,
  color: 'rgba(100, 116, 139, 0.34)',
  segmentHeight: 36,
};

export function buildCanvasGridStyle(pan) {
  const { backgroundColor, lineColor, size } = WORKFLOW_CANVAS_GRID;
  return {
    backgroundColor,
    backgroundImage: `linear-gradient(${lineColor} 1px, transparent 1px), linear-gradient(90deg, ${lineColor} 1px, transparent 1px)`,
    backgroundSize: `${size}px ${size}px`,
    ...buildGridBackgroundStyle(pan),
  };
}

export function clampZoom(zoom) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(zoom)));
}

export function zoomScale(zoom) {
  return clampZoom(zoom) / 100;
}

export function isEditableTarget(target) {
  if (!target || typeof target.closest !== 'function') return false;
  return Boolean(
    target.closest('input, textarea, select, [contenteditable="true"], [contenteditable=""]'),
  );
}

export function shouldIgnoreCanvasPan(target) {
  if (!target || typeof target.closest !== 'function') return false;
  return Boolean(
    target.closest([
      '[data-workflow-editor-ignore-pan]',
      '[data-workflow-toolbar]',
      '[data-workflow-side-panel]',
      '[data-workflow-card]',
      '[data-workflow-card-body]',
      '[data-workflow-card-action]',
      '[data-workflow-trigger-card]',
      '[data-workflow-trigger-action]',
      '[data-workflow-step-card]',
      '[data-workflow-canvas-interactive]',
      '[data-workflow-connector]',
      '[data-workflow-drag-handle]',
      '[role="menu"]',
      '[role="listbox"]',
      '[role="dialog"]',
      '[data-radix-popper-content-wrapper]',
      'button',
      'a',
      'input',
      'textarea',
      'select',
      '[role="menuitem"]',
    ].join(', ')),
  );
}

/** Wheel should not pan when scrolling editors, panels, or form controls. */
export function shouldIgnoreCanvasWheel(target) {
  if (!target || typeof target.closest !== 'function') return false;
  if (isEditableTarget(target)) return true;
  return Boolean(
    target.closest([
      '[data-workflow-right-panel]',
      '[data-workflow-side-panel]',
      '[data-workflow-editor-panel-slot]',
      '[data-workflow-canvas-controls]',
      '[role="dialog"]',
      '[role="listbox"]',
      '[data-radix-popper-content-wrapper]',
      'input',
      'textarea',
      'select',
    ].join(', ')),
  );
}

/**
 * Resolve trackpad/mouse wheel deltas for canvas panning.
 * Shift+wheel maps vertical delta to horizontal pan when deltaX is absent.
 */
export function computeWheelPanDelta(event) {
  let deltaX = event.deltaX || 0;
  let deltaY = event.deltaY || 0;

  if (event.shiftKey && deltaX === 0 && deltaY !== 0) {
    deltaX = deltaY;
    deltaY = 0;
  }

  return { deltaX, deltaY };
}

/** Apply wheel deltas to pan, adjusting for zoom so movement feels consistent. */
export function applyWheelPan(pan, { deltaX, deltaY }, zoom = DEFAULT_ZOOM) {
  const scale = zoomScale(zoom) || 1;
  return {
    x: pan.x - deltaX / scale,
    y: pan.y - deltaY / scale,
  };
}

/**
 * Process a wheel event for the workflow canvas viewport.
 * Returns next pan/zoom and whether the event was handled.
 */
export function processCanvasWheelEvent(event, { zoom, pan, getViewportSize }) {
  if (shouldIgnoreCanvasWheel(event.target)) {
    return { handled: false, preventDefault: false, pan, zoom };
  }

  if (event.ctrlKey || event.metaKey) {
    const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    const nextZoom = clampZoom(zoom + delta);
    const { width, height } = getViewportSize();
    let nextPan = pan;
    if (width && height) {
      nextPan = computeZoomAroundCenter({
        zoom,
        nextZoom,
        pan,
        viewportWidth: width,
        viewportHeight: height,
      }).pan;
    }
    return {
      handled: true,
      preventDefault: true,
      pan: nextPan,
      zoom: nextZoom,
    };
  }

  const { deltaX, deltaY } = computeWheelPanDelta(event);
  if (deltaX === 0 && deltaY === 0) {
    return { handled: false, preventDefault: false, pan, zoom };
  }

  return {
    handled: true,
    preventDefault: true,
    pan: applyWheelPan(pan, { deltaX, deltaY }, zoom),
    zoom,
  };
}

export function computeZoomAroundCenter({
  zoom,
  nextZoom,
  pan,
  viewportWidth,
  viewportHeight,
}) {
  const oldScale = zoomScale(zoom);
  const newScale = zoomScale(nextZoom);
  const centerX = viewportWidth / 2;
  const centerY = viewportHeight / 2;
  const worldX = (centerX - pan.x) / oldScale;
  const worldY = (centerY - pan.y) / oldScale;
  return {
    zoom: clampZoom(nextZoom),
    pan: {
      x: centerX - worldX * newScale,
      y: centerY - worldY * newScale,
    },
  };
}

export function computeFitTransform({
  bounds,
  viewportWidth,
  viewportHeight,
  padding = FIT_PADDING,
}) {
  if (!viewportWidth || !viewportHeight) {
    return { zoom: DEFAULT_ZOOM, pan: { x: 0, y: 0 } };
  }

  if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
    const contentWidth = 560;
    const contentHeight = 220;
    return {
      zoom: DEFAULT_ZOOM,
      pan: {
        x: Math.max(padding, (viewportWidth - contentWidth) / 2),
        y: Math.max(padding, (viewportHeight - contentHeight) / 2),
      },
    };
  }

  const availableWidth = Math.max(1, viewportWidth - padding * 2);
  const availableHeight = Math.max(1, viewportHeight - padding * 2);
  const scale = Math.min(
    availableWidth / bounds.width,
    availableHeight / bounds.height,
    MAX_ZOOM / 100,
  );
  const zoom = clampZoom(Math.floor(scale * 100));
  const nextScale = zoomScale(zoom);
  const panX = (viewportWidth - bounds.width * nextScale) / 2 - bounds.left * nextScale;
  const panY = (viewportHeight - bounds.height * nextScale) / 2 - bounds.top * nextScale;

  return {
    zoom,
    pan: { x: panX, y: panY },
  };
}

function mergeRectBounds(bounds, rect, containerRect) {
  const left = rect.left - containerRect.left;
  const top = rect.top - containerRect.top;
  const right = left + rect.width;
  const bottom = top + rect.height;

  if (!Number.isFinite(bounds.minX)) {
    return { minX: left, minY: top, maxX: right, maxY: bottom };
  }

  return {
    minX: Math.min(bounds.minX, left),
    minY: Math.min(bounds.minY, top),
    maxX: Math.max(bounds.maxX, right),
    maxY: Math.max(bounds.maxY, bottom),
  };
}

export function measureContentBounds(contentEl) {
  if (!contentEl) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }

  const nodes = contentEl.querySelectorAll('[data-workflow-canvas-node]');
  const graphBounds = contentEl.querySelector('[data-workflow-graph-bounds]');
  const previousTransform = contentEl.style.transform;
  contentEl.style.transform = 'none';
  const containerRect = contentEl.getBoundingClientRect();

  let bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };

  nodes.forEach((node) => {
    bounds = mergeRectBounds(bounds, node.getBoundingClientRect(), containerRect);
  });

  if (graphBounds) {
    bounds = mergeRectBounds(bounds, graphBounds.getBoundingClientRect(), containerRect);
  }

  contentEl.style.transform = previousTransform;

  if (!Number.isFinite(bounds.minX)) {
    return { left: 0, top: 0, width: 560, height: 220 };
  }

  return {
    left: bounds.minX,
    top: bounds.minY,
    width: Math.max(1, bounds.maxX - bounds.minX),
    height: Math.max(1, bounds.maxY - bounds.minY),
  };
}

export function computePannedPosition(originPan, startX, startY, clientX, clientY) {
  return {
    x: originPan.x + (clientX - startX),
    y: originPan.y + (clientY - startY),
  };
}

/** Shift pan horizontally when the viewport width changes so content stays centered. */
export function adjustPanForViewportWidthChange(pan, previousWidth, nextWidth) {
  if (!pan || !previousWidth || previousWidth === nextWidth) return pan;
  const delta = (nextWidth - previousWidth) / 2;
  return {
    ...pan,
    x: pan.x + delta,
  };
}

export function buildContentTransformStyle(pan, zoom) {
  const scale = zoomScale(zoom);
  return {
    transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
    transformOrigin: '0 0',
  };
}

export function buildGridBackgroundStyle(pan) {
  return {
    backgroundPosition: `${pan.x}px ${pan.y}px`,
  };
}
